"use client";
import { JSX, useEffect, useState } from "react";
import clsx from "clsx";
import { SURFACE_REVEAL } from "@/components/ui";

export const FAQ_OPEN_EVENT = "faq-open";


export type FAQItem = {
    id: string;
    question: string | JSX.Element;
    answer: string | JSX.Element;
    category?: string; // optional for tabs or filters
    media?: string; // optional image/video URL
};

export type FAQType =
    | "accordion"
    | "list"
    | "searchable"
    | "tabbed"
    | "cards"
    | "chatbot";

export type FAQProps = {
    type?: FAQType;
    items: FAQItem[];
    categories?: string[]; // for tabbed or searchable
    /** Prefix for element ids (`{prefix}-{item.id}`) — used for in-page search anchors. */
    itemIdPrefix?: string;
};

const FAQ: React.FC<FAQProps> = ({ type = "accordion", items, categories, itemIdPrefix }) => {
    const [openIds, setOpenIds] = useState<string[]>([]);
    const [activeCategory, setActiveCategory] = useState<string>(categories?.[0] || "");
    const [search, setSearch] = useState("");

    const toggle = (id: string) => {
        setOpenIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    useEffect(() => {
        const onOpen = (event: Event) => {
            const id = (event as CustomEvent<{ id: string }>).detail?.id;
            if (!id) return;
            setOpenIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
        };
        window.addEventListener(FAQ_OPEN_EVENT, onOpen);
        return () => window.removeEventListener(FAQ_OPEN_EVENT, onOpen);
    }, []);

    // Highlight search term in text
    const highlightText = (text: string, term: string) => {
        if (!term) return text;
        const parts = text.split(new RegExp(`(${term})`, "gi"));
        return parts.map((part, i) =>
            part.toLowerCase() === term.toLowerCase() ? (
                <mark key={i} className="bg-yellow-200">
                    {part}
                </mark>
            ) : (
                part
            )
        );
    };

    // Filter items based on category and search
    const filteredItems = items.filter(
        (item) =>
            (!activeCategory || item.category === activeCategory || type !== "tabbed") &&
            item.question.toString().toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="w-full max-w-5xl mx-auto p-4">
            {/* Searchable */}
            {type === "searchable" && (
                <input
                    type="text"
                    placeholder="Search FAQs..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full p-2 mb-4 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
            )}

            {/* Tabbed Categories */}
            {type === "tabbed" && categories && (
                <div className="flex flex-wrap mb-4 gap-2">
                    {categories.map((cat) => (
                        <button
                            key={cat}
                            className={clsx(
                                "px-4 py-2 rounded-md transition",
                                activeCategory === cat
                                    ? "bg-blue-600 text-white"
                                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                            )}
                            onClick={() => setActiveCategory(cat)}
                        >
                            {cat}
                        </button>
                    ))}
                </div>
            )}

            {/* FAQ Items */}
            <div
                className={clsx(
                    type === "cards" ? "grid md:grid-cols-2 gap-4" : "flex flex-col gap-2"
                )}
            >
                {filteredItems.map((item) => (
                    <div
                        key={item.id}
                        id={itemIdPrefix ? `${itemIdPrefix}-${item.id}` : item.id}
                        className={clsx(
                            type === "accordion"
                                ? clsx(
                                      "page-glass-panel page-glass-panel--elevated overflow-hidden p-0",
                                      SURFACE_REVEAL
                                  )
                                : "border border-[color-mix(in_srgb,var(--semantic-border-default)_38%,transparent)] rounded-2xl p-4 transition-all",
                            type === "cards" && "surface-interactive hover:shadow-lg"
                        )}
                    >
                        {/* Header */}
                        {(type === "accordion" || type === "cards" || type === "list") && (
                            <button
                                type="button"
                                onClick={() => toggle(item.id)}
                                className="faq-accordion-trigger flex w-full items-center justify-between gap-3 px-4 py-4 sm:px-5"
                            >
                                {typeof item.question === "string"
                                    ? highlightText(item.question, search)
                                    : item.question}
                                {type !== "list" && (
                                    <span
                                        className={clsx(
                                            "ml-2 transition-transform",
                                            openIds.includes(item.id) && "rotate-45"
                                        )}
                                    >
                                        +
                                    </span>
                                )}
                            </button>
                        )}

                        {/* Tabbed */}
                        {type === "tabbed" && (
                            <div className="font-medium">
                                {typeof item.question === "string"
                                    ? highlightText(item.question, search)
                                    : item.question}
                            </div>
                        )}

                        {/* Answer */}
                        {(type === "list" ||
                            type === "searchable" ||
                            openIds.includes(item.id) ||
                            type === "tabbed") && (
                                <div
                                    className={clsx(
                                        type === "accordion"
                                            ? "mt-0 border-t border-[color-mix(in_srgb,var(--semantic-border-default)_28%,transparent)] px-4 pb-4 pt-3 text-sm leading-relaxed text-prose-muted sm:px-5"
                                            : "mt-2 text-prose-muted"
                                    )}
                                >
                                    {typeof item.answer === "string"
                                        ? highlightText(item.answer, search)
                                        : item.answer}
                                    {item.media && (
                                        <div className="mt-2">
                                            <img
                                                src={item.media}
                                                alt="FAQ media"
                                                className="max-w-full rounded-md"
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                    </div>
                ))}

                {filteredItems.length === 0 && (
                    <p className="text-gray-500">
                        {type === "tabbed"
                            ? `No FAQs in "${activeCategory}"`
                            : "No FAQs found."}
                    </p>
                )}
            </div>
        </div>
    );
};

export default FAQ;
